/**
 * @file interpret/engine/pcm16.ts
 * @brief Browser mic samples → the 16 kHz PCM16 frames the interpreter WS expects.
 *
 * @details
 * The backend (`BACKEND/src/websocket/server.mjs`, `encoding: "pcm16"`) takes raw
 * 16 kHz mono 16-bit little-endian audio, base64 per message, and feeds it to the
 * server-side Silero VAD. The phone's native VZAudio produces exactly that; a
 * browser mic runs at 44.1 or 48 kHz Float32, so we resample here.
 *
 * Why not `new AudioContext({ sampleRate: 16000 })` and let the browser resample:
 * Firefox has refused to connect a mic source to a context at a different rate,
 * and the behaviour varies by version. Doing it ourselves is identical everywhere.
 *
 * The filter is a short box average around each output point (3 taps at 48 kHz),
 * enough to keep speech energy above 8 kHz from folding back into the band ASR
 * and Silero listen to. Pure and streaming: state carries across `push()` calls
 * so frame boundaries never click.
 *
 * Frames are 20 ms (320 samples), the same packet size the phone sends.
 *
 * @references
 * - docs/product/web-interpretation-plan.md (Phase 1)
 * - FRONTEND/src/screens/S2SInterpreter/hooks/useTurnInterpreter.ts (float32ToPCM16LE)
 *
 * @author DES
 * @date 01-10-2026
 */

export const S2S_SAMPLE_RATE = 16000;
/** 20 ms at 16 kHz, the phone's packet size. */
export const S2S_FRAME_SAMPLES = 320;

export class Pcm16Resampler {
  private readonly ratio: number;
  private readonly halfWidth: number;
  private readonly frameSamples: number;
  /** Input not yet consumed, including `halfWidth` samples of history. */
  private pending: Float32Array = new Float32Array(0);
  /** Position of the next output sample, in `pending` indices. */
  private pos: number;
  private frame: Int16Array;
  private frameFill = 0;

  constructor(
    inputRate: number,
    outputRate: number = S2S_SAMPLE_RATE,
    frameSamples: number = S2S_FRAME_SAMPLES,
  ) {
    if (!(inputRate > 0) || !(outputRate > 0) || outputRate > inputRate) {
      throw new Error(`Pcm16Resampler: cannot resample ${inputRate} → ${outputRate}`);
    }
    this.ratio = inputRate / outputRate;
    this.halfWidth = Math.floor(Math.round(this.ratio) / 2);
    this.pos = this.halfWidth;
    this.frameSamples = frameSamples;
    this.frame = new Int16Array(frameSamples);
  }

  /** Feed mic samples; returns every complete frame they finish (often none). */
  push(input: Float32Array): Int16Array[] {
    const work = new Float32Array(this.pending.length + input.length);
    work.set(this.pending, 0);
    work.set(input, this.pending.length);

    const out: Int16Array[] = [];
    const hw = this.halfWidth;
    let pos = this.pos;
    while (Math.round(pos) + hw < work.length) {
      const centre = Math.round(pos);
      let sum = 0;
      for (let j = centre - hw; j <= centre + hw; j += 1) sum += work[j] ?? 0;
      this.frame[this.frameFill] = floatToInt16(sum / (2 * hw + 1));
      this.frameFill += 1;
      if (this.frameFill === this.frameSamples) {
        out.push(this.frame);
        this.frame = new Int16Array(this.frameSamples);
        this.frameFill = 0;
      }
      pos += this.ratio;
    }

    // Keep what the next output still needs (its window reaches back `hw`).
    const drop = Math.max(0, Math.min(work.length, Math.floor(pos) - hw));
    this.pending = work.slice(drop);
    this.pos = pos - drop;
    return out;
  }

  /** Forget buffered audio (after a pause, so old speech never leads a new turn). */
  reset(): void {
    this.pending = new Float32Array(0);
    this.pos = this.halfWidth;
    this.frame = new Int16Array(this.frameSamples);
    this.frameFill = 0;
  }
}

export function floatToInt16(s: number): number {
  const c = s > 1 ? 1 : s < -1 ? -1 : s || 0;
  return Math.round(c < 0 ? c * 0x8000 : c * 0x7fff);
}

/** Little-endian bytes as base64 (every browser we ship to is little-endian). */
export function int16ToBase64(frame: Int16Array): string {
  const bytes = new Uint8Array(frame.buffer, frame.byteOffset, frame.byteLength);
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

/** RMS of a frame, 0..1. Drives the mic glow only, never turn decisions. */
export function frameRms(frame: Int16Array): number {
  if (frame.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < frame.length; i += 1) {
    const v = (frame[i] ?? 0) / 0x8000;
    sum += v * v;
  }
  return Math.sqrt(sum / frame.length);
}
